import { theme } from 'antd';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { INTEGRATION_TABS, ORGANISATION_TABS, STATIC_KEYS } from 'config/settingsMenuConfig';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { settingsActions } from 'reduxStore/settings/slice';
import { selectCurrentProfileOrgSettings } from 'reduxStore/settings/selectors';
import { Col, Empty, Flex, Row } from 'zui/Antd';
import RolesSettings from '../RolesSettings';
import SitesAndFloorsSettings from '../SitesAndFloorsSettings';
import { RenderContentSetting } from './components';
import MenuWhite from './settings.style';
import ProductsPage from './components/ProductsPage';
import IntegrationsPage from './components/IntegrationsPage';

const RenderOrganisationSettings = () => {
	const {
		token: { colorBorder, marginXL, marginSM, marginMD }
	} = theme.useToken();
	const dispatch = useDispatch();

	const { view } = useSelector((state) => state.App);
	const { selectedSettingGroupID, personSettings } = useSelector((state) => state.Settings);
	const { groupSettingsData } = useSelector((state) => state.DefaultSettings);
	const orgSettings = useSelector(selectCurrentProfileOrgSettings);

	// ── Filter tabs by their moduleFlag ─────────────────────────────────────
	// Convention: Number(value) === 1 is ON. No flag = always visible.
	const { visibleOrgTabs, visibleIntegrationTabs } = useMemo(() => {
		const isOn = (flag) => {
			if (!flag) return true;
			const row = orgSettings.find((s) => s.defaultSettingID === flag);
			return Number(row?.value) === 1;
		};

		return {
			visibleOrgTabs: ORGANISATION_TABS.filter((t) => isOn(t.moduleFlag)),
			visibleIntegrationTabs: INTEGRATION_TABS.filter((t) => isOn(t.moduleFlag))
		};
	}, [orgSettings]);

	// All tabs that live under the Organisation group in the menu.
	// Org tabs come first (General, Access, Lockers), then active integrations.
	const organisationChildren = useMemo(
		() => [...visibleOrgTabs, ...visibleIntegrationTabs],
		[visibleOrgTabs, visibleIntegrationTabs]
	);

	// ── Valid selection keys (for fallback logic) ───────────────────────────
	const validKeys = useMemo(() => {
		const keys = new Set([
			STATIC_KEYS.PROFILE,
			STATIC_KEYS.PRODUCTS,
			STATIC_KEYS.INTEGRATIONS,
			STATIC_KEYS.ROLES,
			STATIC_KEYS.SITES
		]);
		organisationChildren.forEach((t) => {
			keys.add(t.key);
			keys.add(String(t.key));
		});
		return keys;
	}, [organisationChildren]);

	// ── Default selection + fallback when current selection disappears ──────
	useEffect(() => {
		if (!selectedSettingGroupID) {
			if (Object.keys(personSettings).length > 0) {
				dispatch(settingsActions.setSelectedSettingGroup(STATIC_KEYS.PROFILE));
			} else {
				dispatch(settingsActions.setSelectedSettingGroup(STATIC_KEYS.PRODUCTS));
			}
			return;
		}

		// Admin turned off the module whose settings tab they were viewing
		const selectedStr = String(selectedSettingGroupID);
		if (!validKeys.has(selectedSettingGroupID) && !validKeys.has(selectedStr)) {
			dispatch(settingsActions.setSelectedSettingGroup(STATIC_KEYS.PRODUCTS));
		}
	}, [selectedSettingGroupID, personSettings, validKeys, dispatch]);

	// ── Menu builders ───────────────────────────────────────────────────────
	const select = (key) => () => {
		dispatch(settingsActions.setSelectedSettingGroup(key));
	};

	const toMenuItem = ({ key, label }) => ({
		key: String(key),
		label,
		onClick: select(key)
	});

	const menuItems = [
		// Profile — only if personSettings exist
		...(Object.keys(personSettings).length > 0
			? [{ key: STATIC_KEYS.PROFILE, label: 'Profile', onClick: select(STATIC_KEYS.PROFILE) }]
			: []),

		// Products — clickable, always visible
		{ key: STATIC_KEYS.PRODUCTS, label: 'Products', onClick: select(STATIC_KEYS.PRODUCTS) },

		// Integrations — clickable, always visible (sibling of Products)
		{
			key: STATIC_KEYS.INTEGRATIONS,
			label: 'Integrations',
			onClick: select(STATIC_KEYS.INTEGRATIONS)
		},

		// Organisation — expand-only, absorbs org tabs + active integration tabs
		{
			key: 'organisation',
			label: 'Organisation',
			children: organisationChildren.map(toMenuItem)
		},

		{ key: STATIC_KEYS.ROLES, label: 'Admin Roles', onClick: select(STATIC_KEYS.ROLES) },
		{ key: STATIC_KEYS.SITES, label: 'Site Management', onClick: select(STATIC_KEYS.SITES) }
	];

	// ── Content router ──────────────────────────────────────────────────────
	const renderContent = () => {
		if (selectedSettingGroupID === STATIC_KEYS.PRODUCTS) {
			return <ProductsPage />;
		}

		if (selectedSettingGroupID === STATIC_KEYS.INTEGRATIONS) {
			return <IntegrationsPage />;
		}

		if (selectedSettingGroupID === STATIC_KEYS.ROLES) {
			return <RolesSettings />;
		}

		if (selectedSettingGroupID === STATIC_KEYS.SITES) {
			return <SitesAndFloorsSettings />;
		}

		if (selectedSettingGroupID === CONSTANTS_SETTINGS.PERSON_SETTING_TYPE) {
			return (
				<RenderContentSetting
					settingGroupID={CONSTANTS_SETTINGS.PERSON_SETTING_TYPE}
					entityType={CONSTANTS_SETTINGS.PERSON_SETTING_TYPE}
				/>
			);
		}

		if (groupSettingsData.length > 0 && groupSettingsData.some((a) => a.groupID === selectedSettingGroupID)) {
			return (
				<RenderContentSetting
					settingGroupID={selectedSettingGroupID}
					entityType={CONSTANTS_SETTINGS.ORG_SETTING_TYPE}
				/>
			);
		}

		return (
			<Flex justify='center' align='center' flex={1}>
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'No Settings created.'} />
			</Flex>
		);
	};

	return (
		<Row
			style={{
				height: 'calc(100% + 30px)',
				margin: `-15px -${marginSM}px -15px -${marginXL}px`
			}}
			wrap
		>
			<Col md={3} xs={24}>
				<MenuWhite
					mode={view === 'DesktopView' ? 'inline' : 'vertical'}
					inlineCollapsed={false}
					defaultOpenKeys={['organisation']}
					selectedKeys={[String(selectedSettingGroupID)]}
					items={menuItems}
					style={{
						borderRight: view === 'DesktopView' ? `1px solid ${colorBorder}` : 'none',
						width: '100%',
						height: view === 'DesktopView' ? '100%' : 'auto'
					}}
				/>
			</Col>

			<Col
				md={21}
				style={{
					padding: `${marginMD}px ${marginSM}px 0px ${marginXL}px`
				}}
			>
				{renderContent()}
			</Col>
		</Row>
	);
};

export default RenderOrganisationSettings;
